import React, { useState, useEffect, useCallback } from "react";
import {
  CloseIcon,
  MapPinIcon,
  SpinnerIcon,
  SearchIcon,
} from "../../Icons";
import { request } from "../../utils/api";
import toast from "react-hot-toast";
import { assignUserCoverageAreaBodyRequest } from "../../utils/coverageArea.validation";
import {
  COVERAGE_THEME,
  COVERAGE_BTN,
  COVERAGE_FIELD,
  COVERAGE_CHECKBOX,
  COVERAGE_MODAL,
} from "../../pages/coverageAreaTheme";

/**
 * Modal for managing user coverage areas (city selection).
 * Uses GET/PUT /users/me/coverage-areas. When admin edits another user, passes userId as query (GET) or in body (PUT).
 */
const CoverageAreaModal = ({ isOpen, onClose, onSuccess, userId, subjectName }) => {
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const filteredCities = cities.filter((c) =>
    c.name.toLowerCase().includes(citySearch.toLowerCase()),
  );

  const coveragePath = "/users/me/coverage-areas";
  const getCoverageUrl = userId
    ? `${coveragePath}?userId=${encodeURIComponent(userId)}`
    : coveragePath;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [citiesRes, coverageRes] = await Promise.all([
        request("/users/cities"),
        request(getCoverageUrl),
      ]);

      if (citiesRes?.status === 1 && citiesRes?.result?.cities) {
        setCities(citiesRes.result.cities);
      } else {
        toast.error(citiesRes?.message || "Failed to load cities");
      }

      if (coverageRes?.status === 1 && coverageRes?.result?.coverageAreas) {
        const ids = new Set(
          coverageRes.result.coverageAreas.map((area) => area.id),
        );
        setSelectedIds(ids);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load coverage areas");
    } finally {
      setLoading(false);
    }
  }, [getCoverageUrl]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      setCities([]);
      setCitySearch("");
      setSelectedIds(new Set());
    }
  }, [isOpen, fetchData]);

  const toggleCity = (cityId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cityId)) {
        next.delete(cityId);
      } else {
        next.add(cityId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === cities.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cities.map((c) => c.id)));
    }
  };

  const handleSave = async () => {
    const cityIds = Array.from(selectedIds).sort((a, b) => a - b);
    const payload = assignUserCoverageAreaBodyRequest.parse({
      ...(userId && { userId: Number(userId) }),
      cityIds,
    });
    setSaving(true);
    try {
      const response = await request(coveragePath, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (response?.status === 1) {
        toast.success(
          response?.message || "Coverage areas updated successfully",
        );
        onSuccess?.();
        onClose();
      } else {
        toast.error(response?.message || "Failed to update coverage areas");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update coverage areas");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={COVERAGE_MODAL.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="coverage-area-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className={COVERAGE_MODAL.panel}>
        <div className={COVERAGE_MODAL.head}>
          <div className="flex items-start gap-3 min-w-0">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <MapPinIcon size={18} />
            </span>
            <div className="min-w-0">
              <h2 id="coverage-area-title" className={COVERAGE_MODAL.title}>
                Coverage Area
              </h2>
              <p className={COVERAGE_MODAL.subtitle}>
                {userId && subjectName
                  ? `Update coverage area for ${subjectName}`
                  : "Select the cities in your coverage area"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={COVERAGE_MODAL.close}
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className={COVERAGE_MODAL.body}>
          {loading ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-3">
                <div className="h-4 w-48 bg-indigo-100 rounded animate-pulse" />
                <div className="h-4 w-24 bg-indigo-100 rounded animate-pulse" />
              </div>
              <div className="h-8 bg-indigo-50 rounded-md animate-pulse mb-2" />
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border border-indigo-100 rounded-md p-2 ${COVERAGE_THEME.section.wrap}`}
              >
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-2 py-1 animate-pulse"
                  >
                    <div className="w-4 h-4 rounded bg-indigo-100 shrink-0" />
                    <div className="h-4 bg-indigo-100 rounded flex-1 max-w-[70%]" />
                  </div>
                ))}
              </div>
            </div>
          ) : cities.length === 0 ? (
            <p className="text-center text-slate-500 py-12 text-sm">
              No cities available.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-slate-600">
                  Choose one or more cities
                </p>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className={COVERAGE_BTN.link}
                >
                  {selectedIds.size === cities.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              <div className="space-y-2">
                <div className="relative">
                  <SearchIcon
                    size={14}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    placeholder="Search cities..."
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    className={COVERAGE_FIELD.searchSm}
                  />
                </div>
                <div className={COVERAGE_CHECKBOX.listBox}>
                  {filteredCities.length === 0 ? (
                    <p className="col-span-full text-xs text-slate-500 py-2 px-2">
                      {citySearch
                        ? "No matches found"
                        : "No cities available."}
                    </p>
                  ) : (
                    filteredCities.map((city) => {
                      const isSelected = selectedIds.has(city.id);
                      return (
                        <label
                          key={city.id}
                          className={`flex items-center gap-2 cursor-pointer rounded px-2 py-1 transition-colors ${
                            isSelected
                              ? COVERAGE_CHECKBOX.wrapSelected
                              : COVERAGE_CHECKBOX.wrapIdle
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCity(city.id)}
                            className={COVERAGE_CHECKBOX.input}
                          />
                          <span
                            className={
                              isSelected
                                ? COVERAGE_CHECKBOX.labelSelected
                                : COVERAGE_CHECKBOX.labelIdle
                            }
                          >
                            {city.name}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {selectedIds.size > 0 ? (
                <p className="text-[11px] text-slate-500 tabular-nums">
                  {selectedIds.size} of {cities.length} selected
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className={COVERAGE_MODAL.footer}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={COVERAGE_BTN.modalCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className={COVERAGE_BTN.modalPrimary}
          >
            {saving ? (
              <>
                <SpinnerIcon className="animate-spin" size={14} />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoverageAreaModal;
